// pages/MotsIncomplets.jsx
// Page de gestion des mots avec données manquantes

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertCircle, Search, ArrowLeft, Sparkles } from 'lucide-react';
import GestionMots from '../components/GestionMots';
import './MotsIncomplets.css';

export default function MotsIncomplets() {
  const location = useLocation();
  const [showResults, setShowResults] = useState(false);
  const [langue, setLangue] = useState('fr');
  const [typeMot, setTypeMot] = useState('tous');
  const [mots, setMots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contextes, setContextes] = useState([]);

  // États pour l'enrichissement IA
  const [limiteMots, setLimiteMots] = useState(50);
  const [enrichissementEnCours, setEnrichissementEnCours] = useState(false);
  const [enrichissementResultat, setEnrichissementResultat] = useState(null);
  const [typePluriel, setTypePluriel] = useState('inoperant');
  const [typeGenre, setTypeGenre] = useState('inoperant');

  // Réinitialiser l'écran 1 quand on navigue vers cette page depuis le menu
  useEffect(() => {
    if (location.state?.resetToScreen1) {
      setShowResults(false);
    }
  }, [location.state]);

  // Charger les contextes au montage
  useEffect(() => {
    loadContextes();
  }, []);

  // Charger la liste des contextes prédéfinis
  const loadContextes = async () => {
    try {
      const response = await fetch('/api/contextes');
      const data = await response.json();

      if (data.success) {
        const contextesTries = [...(data.contextes || [])].sort((a, b) => {
          if (a.value === '') return -1;
          if (b.value === '') return 1;
          return a.label.localeCompare(b.label, 'fr');
        });
        setContextes(contextesTries);
      }
    } catch (err) {
      console.error('Erreur chargement contextes:', err);
    }
  };

  // Rechercher les mots incomplets
  const handleRechercher = async () => {
    setLoading(true);
    setError(null);
    setShowResults(true);

    try {
      // Construire l'URL selon le type de mot
      let url = `/api/mots/incomplets?typeMot=${typeMot}`;

      // Ajouter la langue si différente de "toutes"
      if (langue !== 'toutes') {
        url += `&langue=${langue}`;
      }

      // Ajouter le type de pluriel si différent de "inoperant"
      if (typePluriel !== 'inoperant') {
        url += `&typePluriel=${typePluriel}`;
      }

      // Ajouter le type de genre si différent de "non-defini"
      if (typeGenre !== 'non-defini') {
        url += `&typeGenre=${typeGenre}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la recherche');
      }

      setMots(data.mots || []);
      } catch (err) {
      console.error('Erreur recherche mots incomplets:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Recharger les résultats
  const handleReload = () => {
    handleRechercher();
  };

  // Retour à l'écran de recherche
  const handleRetourRecherche = () => {
    setShowResults(false);
    setMots([]);
    setError(null);
    setEnrichissementResultat(null);
  };

  // Enrichir les mots avec l'IA
  const handleEnrichirIA = async () => {
    setEnrichissementEnCours(true);
    setError(null);
    setEnrichissementResultat(null);

    // Ajuster la limite au nombre de mots disponibles si nécessaire
    const limiteEffective = Math.min(limiteMots, mots.length);

    if (limiteEffective < limiteMots) {
      console.log(`⚠️ Ajustement de la limite: ${limiteMots} → ${limiteEffective} (nombre de mots disponibles)`);
    }

    try {
      // Préparer le body selon les filtres actifs
      const requestBody = {
        langue,
        typeMot,
        limit: limiteEffective,
        typePluriel,
        typeGenre,
      };

      const response = await fetch('/api/enrichissement/ia', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.details ? `${data.error}: ${data.details}` : data.error || "Erreur lors de l'enrichissement";
        throw new Error(errorMessage);
      }

      setEnrichissementResultat(data);

      // Recharger les résultats si on est déjà sur l'écran des résultats
      if (showResults) {
        handleRechercher();
      } else {
        // Sinon, afficher automatiquement les résultats
        setShowResults(true);
        handleRechercher();
      }
    } catch (err) {
      console.error('Erreur enrichissement IA:', err);
      setError(err.message);
    } finally {
      setEnrichissementEnCours(false);
    }
  };

  // Options de langues
  const langueOptions = [
    { value: 'toutes', label: '🇪🇺 Toutes les langues' },
    { value: 'fr', label: '🇫🇷 Français' },
    { value: 'es', label: '🇪🇸 Espagnol' },
    { value: 'en', label: '🇬🇧 Anglais' },
  ];

  // Options de type de mot
  const typeMotOptions = [
    { value: 'tous', label: '📚 Tous les types' },
    { value: 'nom', label: '📦 Nom' },
    { value: 'adjectif', label: '✨ Adjectif' },
    { value: 'adverbe', label: '⚡ Adverbe' },
    { value: 'pronom', label: '👤 Pronom' },
    { value: 'adjectif_indefini', label: '🔢 Adjectif indéfini' },
    { value: 'expression', label: '💬 Expression' },
    { value: 'sans-type', label: '🏷️ Sans type' },
  ];

  return (
    <div className="mots-incomplets-page">
      {/* Écran de sélection */}
      {!showResults && (
        <div className="selection-screen">
          <div className="page-header">
            <h1>
              <AlertCircle size={32} color="#F59E0B" />
              Mots Incomplets
            </h1>
            <p className="page-description">Trouvez les mots qui ont des données manquantes et complétez-les</p>
          </div>

          <div className="selection-form">
            <div className="form-group">
              <label htmlFor="langue-select">🌍 Langue</label>
              <select id="langue-select" value={langue} onChange={(e) => setLangue(e.target.value)} className="form-select">
                {langueOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="type-mot-select">📋 Type de mot</label>
              <select id="type-mot-select" value={typeMot} onChange={(e) => setTypeMot(e.target.value)} className="form-select">
                {typeMotOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sélecteur Type de pluriel */}
            <div className="form-group">
              <label htmlFor="type-pluriel-select">📊 Type de pluriel</label>
              <select id="type-pluriel-select" value={typePluriel} onChange={(e) => setTypePluriel(e.target.value)} className="form-select">
                <option value="inoperant">🔄 Inopérant (tous les mots)</option>
                <option value="sans-pluriel">❌ Sans pluriel (invariable/singularia tantum/pluralia tantum)</option>
                <option value="avec-pluriel">✅ Avec pluriel (motPluriel non vide)</option>
                <option value="pluriel-omis">⚠️ Pluriel omis (motPluriel vide, mot non invariable)</option>
              </select>
              <p className="form-help">Filtrer les mots selon leur statut de pluriel</p>
            </div>

            {/* Sélecteur Type de genre */}
            <div className="form-group">
              <label htmlFor="type-genre-select">👤 Filtre genre</label>
              <select id="type-genre-select" value={typeGenre} onChange={(e) => setTypeGenre(e.target.value)} className="form-select">
                <option value="inoperant">🔄 Inopérant (tous les mots)</option>
                <option value="sans-genre">❓ Sans genre défini</option>
                <option value="masculin">👨 Masculin</option>
                <option value="feminin">👩 Féminin</option>
                <option value="neutre">⚪ Neutre</option>
              </select>
              <p className="form-help">Filtrer les mots selon leur genre grammatical</p>
            </div>

            <button onClick={handleRechercher} className="btn-rechercher" disabled={enrichissementEnCours}>
              <Search size={20} />
              Rechercher
            </button>
          </div>
        </div>
      )}

      {/* Écran de résultats */}
      {showResults && (
        <div className="resultats-screen">
          <div className="page-header">
            <div className="header-with-back">
              <button onClick={handleRetourRecherche} className="btn-back">
                <ArrowLeft size={20} />
                Nouvelle recherche
              </button>
              <h1>
                <AlertCircle size={32} color="#F59E0B" />
                Mots Incomplets
              </h1>
            </div>
            <p className="page-description">
              {typeMot === 'sans-type' && '🏷️ Mots sans type'}
              {typeMot === 'adverbe' && '📋 Mots adverbes'}
              {typeMot !== 'tous' && typeMot !== 'sans-type' && typeMot !== 'adverbe' && `${typeMotOptions.find((opt) => opt.value === typeMot)?.label}`}
              {typeMot === 'tous' && '📚 Tous types de mots'},
              {langue !== 'toutes' ? ` ${langueOptions.find((l) => l.value === langue)?.label}` : ' 🇪🇺 toutes langues'},
              {typePluriel === 'sans-pluriel' && ' ❌ sans pluriel'}
              {typePluriel === 'avec-pluriel' && ' ✅ avec pluriel'}
              {typePluriel === 'pluriel-omis' && ' ⚠️ pluriel omis'}
              {typePluriel === 'inoperant' && ' 🔄 tous pluriels'},
              {typeGenre === 'sans-genre' && ' ❓ sans genre défini'}
              {typeGenre === 'masculin' && ' 👨 masculin'}
              {typeGenre === 'feminin' && ' 👩 féminin'}
              {typeGenre === 'neutre' && ' ⚪ neutre'}
              {typeGenre === 'inoperant' && ' 👤 tous genres'}
            </p>
          </div>

          {/* Section Enrichissement IA */}
          <div className="enrichissement-section">
            <div className="enrichissement-controls">
              <div className="form-group-inline">
                <label htmlFor="limite-mots-input">🔢 Nombre de mots à enrichir</label>
                <input
                  id="limite-mots-input"
                  type="number"
                  value={limiteMots}
                  onChange={(e) => setLimiteMots(parseInt(e.target.value) || 50)}
                  min="1"
                  max="500"
                  className="form-input"
                />
                {mots.length > 0 && (
                  <p className="form-help" style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#666' }}>
                    {mots.length} mot{mots.length > 1 ? 's' : ''} disponible{mots.length > 1 ? 's' : ''} dans la liste
                  </p>
                )}
              </div>

              <button onClick={handleEnrichirIA} className="btn-enrichir-ia" disabled={enrichissementEnCours || loading || mots.length === 0}>
                <Sparkles size={20} />
                {enrichissementEnCours ? 'Enrichissement en cours...' : "Enrichir avec l'IA"}
              </button>
            </div>

            {enrichissementResultat && (
              <div className="enrichissement-resultat">
                <h3>✅ Enrichissement terminé</h3>
                <p>
                  <strong>{enrichissementResultat.motsEnrichis}</strong> mot{enrichissementResultat.motsEnrichis > 1 ? 's' : ''} enrichi
                  {enrichissementResultat.motsEnrichis > 1 ? 's' : ''} sur {enrichissementResultat.totalTraites} traité
                  {enrichissementResultat.totalTraites > 1 ? 's' : ''}
                </p>

                {/* Liste des mots traités */}
                {enrichissementResultat.motsTraites && enrichissementResultat.motsTraites.length > 0 && (
                  <div className="mots-traites-list">
                    <h4>📝 Mots enrichis :</h4>
                    <ul>
                      {enrichissementResultat.motsTraites.map((mot) => (
                        <li key={mot.id}>
                          <strong>{mot.motSingulier}</strong>
                          {mot.motPluriel && <span> → pluriel: {mot.motPluriel}</span>}
                          {mot.genre && <span> → genre: {mot.genre}</span>}
                          {mot.type && <span> → type: {mot.type}</span>}
                          {mot.sousType && <span> ({mot.sousType})</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {enrichissementResultat.erreurs && enrichissementResultat.erreurs.length > 0 && (
                  <p className="erreurs-count">
                    ⚠️ {enrichissementResultat.erreurs.length} erreur{enrichissementResultat.erreurs.length > 1 ? 's' : ''}
                  </p>
                )}
              </div>
            )}
          </div>

          <GestionMots
            mots={mots}
            loading={loading}
            error={error}
            onReload={handleReload}
            showValiderButton={false}
            contextes={contextes}
            emptyMessage="Aucun mot incomplet trouvé ! 🎉"
            emptyHint="Tous les mots de cette langue ont les données requises."
          />
        </div>
      )}
    </div>
  );
}
